// Fatia S3a: relatório do período (resumo, funil, plataformas, mapa de calor, campeões), PDF, link e resumo semanal.
import { useState } from 'react'
import { Copy, Download, Link2, Mail, Trash2 } from 'lucide-react'
import { errorMessage } from '../../lib/api'
import { useCan, useCompany } from '../../lib/company'
import { ErrorState, Loading, PageHead, Switch, Tabs } from '../../ui'
import { useToast } from '../../ui/toast'
import { NoConnections } from '../../features/insights/components'
import { ReportView } from '../../features/reports/ReportView'
import { downloadPdf, useCreateShareLink, useReport, useReportSchedule, useRevokeShareLink, useSaveReportSchedule, useShareLinks } from '../../features/reports/api'

type Period = '7' | '30' | '90'

export default function Results() {
  const company = useCompany()
  const toast = useToast()
  const canConnect = useCan('company.manage_connections')
  const canSettings = useCan('company.edit_settings')
  const [period, setPeriod] = useState<Period>('30')
  const days = Number(period)
  const q = useReport(company.id, days)
  const [downloading, setDownloading] = useState(false)

  const pdf = async () => {
    setDownloading(true)
    try {
      await downloadPdf(`/companies/${company.id}/reports/pdf?days=${days}`, `relatorio-${days}-dias.pdf`)
    } catch (err) {
      toast(errorMessage(err))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Resultados" sub="O que seu dinheiro em anúncios trouxe de volta, em linguagem simples.">
        <Tabs<Period> value={period} onChange={setPeriod} options={[{ value: '7', label: '7 dias' }, { value: '30', label: '30 dias' }, { value: '90', label: '3 meses' }]} />
        <button className="btn" onClick={pdf} disabled={downloading || !q.data}><Download size={16} /> {downloading ? 'Gerando…' : 'PDF'}</button>
      </PageHead>

      {q.isLoading && <div className="card"><Loading lines={6} /></div>}
      {q.isError && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
      {q.data && !q.data.hasData && (
        <EmptyReport companyId={company.id} canConnect={canConnect} hasConnections={q.data.hasConnections} syncing={q.data.syncing} />
      )}
      {q.data && q.data.hasData && <ReportView report={q.data} />}

      {canSettings && (
        <div className="card card-split">
          <ShareCard days={days} />
          <WeeklySummaryCard />
        </div>
      )}
    </div>
  )
}

function EmptyReport({ companyId, canConnect, hasConnections, syncing }: { companyId: string; canConnect: boolean; hasConnections: boolean; syncing: boolean }) {
  // Só pede para conectar quem ainda não conectou; quem já conectou vê que estamos buscando ou que não houve gasto
  if (!hasConnections) return <NoConnections companyId={companyId} canConnect={canConnect} />
  return (
    <div className="callout" role="status">
      <span>
        {syncing
          ? <><b>Estamos trazendo seus dados.</b> A primeira sincronização pode levar alguns minutos; volte aqui em instantes.</>
          : 'Suas contas estão conectadas, mas não houve gasto nem resultados neste período. Tente um período maior.'}
      </span>
    </div>
  )
}

function ShareCard({ days }: { days: number }) {
  const company = useCompany()
  const toast = useToast()
  const links = useShareLinks(company.id)
  const create = useCreateShareLink(company.id)
  const revoke = useRevokeShareLink(company.id)
  const [url, setUrl] = useState<string | null>(null)
  const active = (links.data?.items ?? []).filter((l) => l.active)

  const copy = async (u: string) => {
    try {
      await navigator.clipboard.writeText(u)
      toast('Link copiado.')
    } catch {
      toast('Não deu para copiar automaticamente. Selecione o link e copie.')
    }
  }

  return (
    <section>
      <div className="row wrap" style={{ gap: 16 }}>
        <span className="ic purple"><Link2 size={18} /></span>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h3>Compartilhar este relatório</h3>
          <p className="small muted">Crie um link para quem não tem acesso ao Seu Impulso (sócio, contador, cliente). Ele mostra só este relatório, vale por 30 dias e pode ser desativado quando quiser.</p>
        </div>
        <button
          className="btn primary"
          disabled={create.isPending}
          onClick={() => create.mutate(days, {
            onSuccess: (r) => { setUrl(r.url); void copy(r.url) },
            onError: (err) => toast(errorMessage(err)),
          })}
        >
          {create.isPending ? 'Criando…' : `Criar link (${days === 90 ? '3 meses' : `${days} dias`})`}
        </button>
      </div>
      {url && (
        <div className="row mt-12" style={{ gap: 8 }}>
          <input className="input" readOnly value={url} onFocus={(e) => e.currentTarget.select()} aria-label="Link do relatório" style={{ minWidth: 0, flex: 1 }} />
          <button className="btn" onClick={() => copy(url)} aria-label="Copiar link"><Copy size={16} /></button>
        </div>
      )}
      {active.length > 0 && (
        <div className="list mt-16">
          {active.map((l) => (
            <div className="list-item" key={l.id} style={{ alignItems: 'center' }}>
              <div style={{ flex: 1 }} className="small">
                Link de {l.periodDays === 90 ? '3 meses' : `${l.periodDays} dias`} · criado em {new Date(l.createdAt).toLocaleDateString('pt-BR')} · vale até {new Date(l.expiresAt).toLocaleDateString('pt-BR')}
              </div>
              <button className="btn sm" disabled={revoke.isPending} onClick={() => revoke.mutate(l.id, { onSuccess: () => toast('Link desativado.'), onError: (err) => toast(errorMessage(err)) })}>
                <Trash2 size={14} /> Desativar
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function WeeklySummaryCard() {
  const company = useCompany()
  const toast = useToast()
  const q = useReportSchedule(company.id, true)
  const save = useSaveReportSchedule(company.id)
  const [extra, setExtra] = useState<string | null>(null)
  const enabled = q.data?.enabled ?? false
  const recipients = q.data?.recipients ?? []
  const text = extra ?? recipients.join(', ')

  const submit = (next: { enabled: boolean; recipientsText?: string }) => {
    const list = (next.recipientsText ?? text).split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean)
    save.mutate({ enabled: next.enabled, recipients: list }, {
      onSuccess: (r) => {
        setExtra(null)
        toast(r.enabled ? 'Pronto! O resumo semanal chega toda segunda-feira de manhã.' : 'Resumo semanal desligado.')
      },
      onError: (err) => toast(errorMessage(err)),
    })
  }

  return (
    <section>
      <div className="row wrap" style={{ gap: 16 }}>
        <span className="ic green"><Mail size={18} /></span>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h3>Resumo toda segunda-feira</h3>
          <p className="small muted">Uma mensagem curta com os números da semana, no app e por e-mail. Também pode ir para o seu sócio ou contador.</p>
        </div>
        {q.isLoading ? <Loading lines={1} /> : <Switch on={enabled} onChange={(v) => submit({ enabled: v })} label="Ligar resumo semanal" disabled={save.isPending} />}
      </div>
      {q.isError && <p className="small mt-8" style={{ color: 'var(--red)' }}>{errorMessage(q.error)}</p>}
      {enabled && (
        <div className="mt-12">
          <label className="small strong" htmlFor="weekly-recipients">Mandar também para (opcional)</label>
          <div className="row mt-8" style={{ gap: 8 }}>
            <input id="weekly-recipients" className="input" placeholder="email@exemplo.com, outro@exemplo.com" value={text} onChange={(e) => setExtra(e.target.value)} style={{ minWidth: 0, flex: 1 }} />
            <button className="btn" disabled={save.isPending || extra == null} onClick={() => submit({ enabled: true })}>Salvar</button>
          </div>
          {q.data?.lastSentAt && <p className="small muted mt-8">Último envio: {new Date(q.data.lastSentAt).toLocaleDateString('pt-BR')}</p>}
        </div>
      )}
    </section>
  )
}
